import {
  PAGE_TYPES,
  PAGE_TYPES_WITH_REFERENCE,
  AD_SCHEDULE_STATUS,
} from "./advertisementConstants";

export const getAdId = (item) => item?.id ?? item?.Id;

export const getAdvertisementName = (item) =>
  item?.advertisementName || item?.AdvertisementName || "-";

export const getAdvertisementPosition = (item) =>
  item?.advertisementPosition || item?.AdvertisementPosition || "-";

export const getPageType = (item) => item?.pageType || item?.PageType || "-";

/** Turn relative/API media paths into absolute URLs for <img src>. */
export const resolveMediaUrl = (url) => {
  if (!url || !String(url).trim()) return "";
  const src = String(url).trim();
  if (/^(https?:|data:|blob:)/i.test(src)) return src;
  const apiBase = process.env.REACT_APP_API_URL || "";
  const origin = apiBase.replace(/\/api\/v\d+\/?$/i, "").replace(/\/$/, "");
  if (!origin) return src;
  const path = src.startsWith("/") ? src : `/${src}`;
  return `${origin}${path}`;
};

export const getImageUrl = (item) => {
  const raw =
    item?.advertisementImageUrl ||
    item?.AdvertisementImageUrl ||
    item?.imageUrl ||
    item?.ImageUrl ||
    item?.thumbnailUrl ||
    item?.ThumbnailUrl ||
    "";
  return resolveMediaUrl(raw);
};

export const getStartDate = (item) => item?.startDate || item?.StartDate;

export const getEndDate = (item) => item?.endDate || item?.EndDate;

export const getReferenceId = (item) =>
  item?.referenceId ?? item?.ReferenceId ?? "";

export const formatDateTimeDisplay = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
};

/**
 * Schedule status for admin display (future: analytics may use same rules).
 */
export const calculateAdScheduleStatus = (startDate, endDate) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (start && !Number.isNaN(start.getTime()) && now < start) {
    return AD_SCHEDULE_STATUS.UPCOMING;
  }
  if (end && !Number.isNaN(end.getTime()) && now > end) {
    return AD_SCHEDULE_STATUS.EXPIRED;
  }
  if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
    if (now >= start && now <= end) {
      return AD_SCHEDULE_STATUS.ACTIVE;
    }
  }
  if (start && !end && !Number.isNaN(start.getTime()) && now >= start) {
    return AD_SCHEDULE_STATUS.ACTIVE;
  }
  if (!start && !end) {
    return AD_SCHEDULE_STATUS.ACTIVE;
  }
  return AD_SCHEDULE_STATUS.ACTIVE;
};

export const getStatusBadgeClass = (status) => {
  switch (status) {
    case AD_SCHEDULE_STATUS.UPCOMING:
      return "warning";
    case AD_SCHEDULE_STATUS.ACTIVE:
      return "success";
    case AD_SCHEDULE_STATUS.EXPIRED:
      return "danger";
    default:
      return "secondary";
  }
};

export const getRedirectUrl = (item) =>
  item?.redirectUrl || item?.RedirectUrl || "-";

export const getDisplayOrder = (item) =>
  item?.displayOrder ?? item?.DisplayOrder ?? "-";

export const getOptionLabel = (options, value) => {
  if (!value) return "-";
  const match = options.find(
    (opt) => opt.value === value || opt.value === String(value)
  );
  return match?.label || value;
};

export const getReferenceDisplay = (item) => {
  const pageType = getPageType(item);
  if (pageType === PAGE_TYPES.HOME) return "—";
  if (pageType === PAGE_TYPES.EXTERNAL) return "—";

  const label =
    item?.referenceName ||
    item?.ReferenceName ||
    item?.sectionName ||
    item?.SectionName ||
    item?.contentTitle ||
    item?.ContentTitle ||
    item?.title ||
    item?.Title;

  const refId = getReferenceId(item);
  if (label) return label;
  if (refId) return `#${refId}`;
  return "—";
};

export const pageTypeRequiresReference = (pageType) =>
  PAGE_TYPES_WITH_REFERENCE.includes(pageType);

export const mapAdvertisementResponse = (data) => ({
  advertisementName:
    data.advertisementName || data.AdvertisementName || "",
  redirectUrl: data.redirectUrl || data.RedirectUrl || "",
  advertisementPosition:
    data.advertisementPosition || data.AdvertisementPosition || "",
  pageType: data.pageType || data.PageType || "",
  referenceId: (() => {
    const ref = data.referenceId ?? data.ReferenceId;
    if (ref === 0 || ref === "0" || ref === null || ref === undefined) {
      return "";
    }
    return String(ref);
  })(),
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
      ? String(data.DisplayOrder)
      : "",
  startDate: data.startDate || data.StartDate || "",
  endDate: data.endDate || data.EndDate || "",
  imagePreview: resolveMediaUrl(
    data.advertisementImageUrl ||
      data.AdvertisementImageUrl ||
      data.imageUrl ||
      ""
  ),
  imageFile: null,
});

export const buildAdvertisementFormData = (formData, isEditMode, id) => {
  const payload = new FormData();

  if (isEditMode && id) {
    payload.append("id", id);
    payload.append("Id", id);
  }

  payload.append("advertisementName", formData.advertisementName.trim());

  if (formData.advertisementImageUrl) {
    payload.append(
      "AdvertisementImageUrl",
      String(formData.advertisementImageUrl)
    );
  }

  if (formData.redirectUrl?.trim()) {
    payload.append("redirectUrl", formData.redirectUrl.trim());
  }

  payload.append("advertisementPosition", formData.advertisementPosition);
  payload.append("pageType", formData.pageType);

  if (pageTypeRequiresReference(formData.pageType) && formData.referenceId) {
    payload.append("referenceId", formData.referenceId);
  } else {
    payload.append("referenceId", "0");
  }

  payload.append("displayOrder", formData.displayOrder);

  if (formData.startDate) {
    payload.append("startDate", formData.startDate);
  }
  if (formData.endDate) {
    payload.append("endDate", formData.endDate);
  }

  return payload;
};

export const mapDropdownOptions = (
  list,
  nameKey,
  pascalNameKey,
  idKey = "id",
  pascalIdKey = "Id"
) =>
  (list || []).map((item) => ({
    value: String(item[idKey] ?? item[pascalIdKey] ?? ""),
    label: item[nameKey] || item[pascalNameKey] || "-",
  }));
